import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Observable, Subscription, catchError, forkJoin, map, of } from "rxjs";
import swal from "sweetalert2";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscAvanceCaptura,
  BscCiclo,
  BscEjercicio,
  BscEjercicioKpi,
  BscEjercicioTrimestre,
  BscKpiMes,
  BscKpiTrimestre,
  BscRes,
  EstadoMesBsc,
  EstatusTrimestreBsc,
  SemaforoBsc,
  ViaCapturaBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { BscService } from "../bsc.service";
import { VERDE_POR_OMISION, alcanceArea, ponderado, ratio, redondear, semaforo } from "../bsc-calculos";
import { ESTADO_MES, ESTATUS_TRIMESTRE, EstadoBsc, SEMAFORO } from "../bsc-estados";
import {
  LecturaNumero,
  SIN_DATO,
  TOPE_REAL_EN_PORCENTAJE,
  diaMes,
  fechaHora,
  formatoNumero,
  leePorcentaje,
  mesEnTexto,
  mesesDeTrimestre,
  meta,
  nombreMes,
  numero,
  pct,
  textoPorcentaje,
} from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn, pesoEn } from "../bsc-kpi";
import { CapturaMesBscComponent, ResultadoCapturaMesBsc, abrirCapturaMesBsc } from "../kpi/captura-mes-bsc.component";

/**
 * Qué enseña la pantalla:
 *   CARGANDO  la primera consulta (o la de otro trimestre, o «Recargar»);
 *   ERROR     previaCierre.do falló (sin la hoja, planeación de otra empresa o
 *             de un área ajena, sin sesión, sin conexión) o el enlace no sirve;
 *   DATOS     el trimestre, en el modo que le toca.
 */
type EstadoPantalla = "CARGANDO" | "ERROR" | "DATOS";

/**
 * Qué se hace con el trimestre:
 *   CIERRE         EN_CAPTURA o REGRESADO de una planeación AUTORIZADA: el asistente de tres pasos;
 *   ENVIADO        ya está en revisión (se acaba de enviar aquí, o ya lo estaba al abrir);
 *   AUTORIZADO     Dirección ya lo autorizó: su alcance quedó congelado;
 *   NO_DISPONIBLE  PENDIENTE, sin renglón o sin planeación autorizada: solo se dice por qué.
 */
type ModoCierre = "CIERRE" | "ENVIADO" | "AUTORIZADO" | "NO_DISPONIBLE";

type PasoCierre = 1 | 2 | 3;

/** Un faltante o un aviso del servidor ("CODIGO[ Qn]: mensaje") listo para mostrarse; el código va en el title. */
interface MensajeCierre {
  codigo: string;
  texto: string;
}

/** Un mes de un KPI en el paso 1, armado UNA vez por respuesta. */
interface CeldaMes {
  mes: number;
  capturado: boolean;
  estado: EstadoBsc<EstadoMesBsc> | null;
  /** "26 sep · Ana López", "Venció el 5 oct", "Sin capturar", "Solo registra actividad". */
  detalle: string;
  /** Lo capturado en una línea (la evidencia o, si no hay, la actividad). */
  resumen: string;
}

/** Un KPI en el paso 1: sus tres meses del trimestre. */
interface FilaMeses {
  idEjercicioKpi: number;
  objetivo: string;
  /** Aplica en el trimestre: sus meses cuentan para cerrar (los NA se listan y se capturan, no cuentan). */
  vigente: boolean;
  nota: string;
  meses: CeldaMes[];
  /** Meses sin capturar; 0 en un NA. */
  faltan: number;
}

/**
 * Un KPI en el paso 2: lo guardado, lo escrito y lo que calcula la previa
 * con lo escrito (K, L contra N y si queda bajo el verde). El real y el
 * programado se escriben en %; viajan como fracción.
 */
interface FilaAvance {
  idEjercicioKpi: number;
  objetivo: string;
  vigente: boolean;
  /** "Acumulado Q2: 47.5% · meta 6 productos" o "Aplica desde Q4 · no cuenta en Q3". */
  nota: string;
  enPorcentaje: boolean;
  /** M del trimestre, fracción. */
  peso: number | null;
  pesoTexto: string;
  /** L del trimestre anterior (0 en Q1). */
  acumAnterior: number;
  /** N del trimestre. */
  acumProg: number | null;

  real: string;
  realInicial: string;
  programado: string;
  programadoInicial: string;
  /** El comentario del avance del trimestre (bsc_kpi_trimestre.comentario): el «resumen de evidencia» de la maqueta. */
  comentario: string;
  comentarioInicial: string;

  /* ---------- Lo que se calcula con lo escrito (recalcula) ---------- */
  /** L del trimestre con el real escrito (sin real, K = 0, como el motor). */
  acumReal: number;
  ponderadoTexto: string;
  acumTexto: string;
  /** L/N menor al verde del ciclo: al enviar se pide el comentario para Dirección. */
  bajoMeta: boolean;
  /** Vigente y sin real escrito. */
  sinReal: boolean;
  ayudaReal: string;
  avisoReal: boolean;
  ayudaProgramado: string;
  avisoProgramado: boolean;

  /* ---------- Lo que dijo el último guardado de esta fila ---------- */
  error: string;
  avisos: MensajeCierre[];
}

/** Un KPI por debajo del verde (kpisBajoMeta de la previa), con su ratio en palabras. */
interface KpiBajoMeta {
  idEjercicioKpi: number;
  objetivo: string;
  texto: string;
}

/** El pie del paso 2: el alcance del área con lo escrito. */
interface PieAlcance {
  real: string;
  programado: string;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  bajoMeta: number;
}

/** Un dato del resultado; el semáforo lleva su badge. */
interface DatoResultado {
  etiqueta: string;
  valor: string;
  badge?: EstadoBsc<SemaforoBsc> | null;
}

/** El trimestre ya enviado o autorizado, como lo dice el resultado. */
interface ResultadoCierre {
  titulo: string;
  icono: string;
  estatus: EstadoBsc<EstatusTrimestreBsc> | null;
  /** En el orden en que se leen. */
  datos: DatoResultado[];
  comentarioTitulo: string;
  comentario: string;
  siguiente: string;
}

const PASOS: { numero: PasoCierre; texto: string }[] = [
  { numero: 1, texto: "Meses revisados" },
  { numero: 2, texto: "Avance real por KPI" },
  { numero: 3, texto: "Confirmar y enviar" },
];

/** Tope de BscCapturaBuss para el comentario del avance y el de envío. */
const LARGO_COMENTARIO = 1000;
/** real.do rechaza una fracción desde 1 000 000. */
const TOPE_PORCENTAJE = 100_000_000;

/** `CODIGO[ Qn]: mensaje` (BscAviso.texto() y los faltantes de BscCapturaBuss). */
const FORMATO_MENSAJE = /^([A-Z][A-Z0-9_-]+)(?: (Q[1-4]))?: ([\s\S]+)$/;

/** Lo que deja la pantalla desactualizada: se ofrece recargar en vez de volver a guardar. */
const ERRORES_DE_RECARGA = new Set(["VAL-BSC-CONFLICTO", "VAL-BSC-VIA", "VAL-BSC-CERRADO", "00002", "AUTH-001"]);

const RUTA = {
  tablero: "/admin/bsc/tablero",
  area: "/admin/bsc/tablero/area",
  miBsc: "/admin/bsc/mi-bsc",
  cargaExcel: "/admin/bsc/carga-excel",
  parametros: "/admin/bsc/parametros",
};

/**
 * BSC · C3 Cierre de trimestre (docs/BSC.md §4 «Trimestre», §7 y Anexo B,
 * maqueta «C3-CierreTrimestre»; F6.5). Ruta
 * `admin/bsc/mi-bsc/cierre/:idEjercicio/:trimestre`; la abren «Cerrar Qn» de
 * C1 y de A2 (con ?desde=area, para que las migas y «Volver» regresen al área).
 *
 * Un asistente lineal de tres pasos sobre previaCierre.do (BscCapturaBuss,
 * que no escribe nada):
 *  1. Meses revisados: los tres meses de cada KPI con su estado; lo que falta
 *     se captura en el mismo cuadro de C1 y A3 (captura-mes-bsc). Si el año
 *     exige los tres meses (exigir_meses_para_cerrar), no se sigue mientras
 *     falte alguno; si no, se sigue con el aviso MESES_INCOMPLETOS.
 *  2. Avance real por KPI: programado, real y el resumen de evidencia (el
 *     comentario del avance) por KPI vigente, con la previa de K, L contra N y
 *     el alcance del área con su semáforo (bsc-calculos.ts, el espejo del
 *     motor). «Guardar borrador» manda por real.do solo lo que cambió; el
 *     trimestre sigue en captura.
 *  3. Confirmar y enviar: lo que dice la previa del servidor (alcance,
 *     faltantes, avisos y los KPI bajo el verde con su ratio), el comentario
 *     para Dirección (obligatorio con algún KPI bajo el verde) y «Enviar a
 *     revisión» con confirmación (cerrar.do). Al enviarse enseña el estatus
 *     que quedó.
 * Un trimestre ENVIADO o AUTORIZADO abre directo en su resultado; uno
 * PENDIENTE, o de una planeación sin autorizar, dice por qué no se cierra.
 *
 * El servidor manda: la previa y el cierre revisan todo otra vez (las fechas
 * con VAL-BSC-FECHAS, el año con VAL-BSC-CERRADO, quién se adelantó con
 * VAL-BSC-CONFLICTO). Lo que se calcula aquí solo guía mientras se escribe.
 *
 * Se captura si: puedeCapturar (hoja mi-bsc y el área en mis manos), la vía
 * del área no es solo EXCEL (de catalogos.do; un área que no sale ahí la
 * decide el servidor), la planeación no viene de un histórico y el año está
 * ABIERTO. Sin captura (un área EXCEL, Dirección sin la hoja mi-bsc) los dos
 * primeros pasos son de lectura y el envío sigue igual: las dos vías terminan
 * en el mismo cierre (§1.1).
 *
 * Base visual: table-page (hero con los pasos como T1, tarjetas bsc-bloque,
 * tablas table-page y campos mat-form-field outline como T3).
 */
@Component({
  selector: "app-cierre-trimestre-bsc",
  standalone: true,
  templateUrl: "./cierre-trimestre-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./cierre-trimestre-bsc.component.css"],
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatFormFieldModule, MatInputModule],
})
export class CierreTrimestreBscComponent implements OnInit, OnDestroy {
  readonly ruta = RUTA;
  readonly pasos = PASOS;
  readonly largoComentario = LARGO_COMENTARIO;

  idEjercicio: number | null = null;
  q = 1;
  desdeArea = false;

  estado: EstadoPantalla = "CARGANDO";
  error = "";
  loading = false;
  modo: ModoCierre = "CIERRE";
  paso: PasoCierre = 1;
  /** Se envió en esta pantalla: el resultado lo celebra en vez de decir «ya estaba». */
  recienEnviado = false;

  ciclo: BscCiclo | null = null;
  ejercicio: BscEjercicio | null = null;
  trimestre: BscEjercicioTrimestre | null = null;
  anio: number | null = null;
  nombreArea = "";
  idBscArea: number | null = null;
  /** La vía del área (catalogos.do); nula si no se supo: entonces decide el servidor. */
  via: ViaCapturaBsc | null = null;

  puedeCapturar = false;
  puedeCerrar = false;
  puedeCargarExcel = false;
  puedeConfigurar = false;
  verTablero = false;

  /* ---------- Lo que se pinta, armado una vez por respuesta ---------- */
  meses: number[] = [];
  filasMeses: FilaMeses[] = [];
  filasAvance: FilaAvance[] = [];
  pie: PieAlcance | null = null;
  faltantes: MensajeCierre[] = [];
  avisos: MensajeCierre[] = [];
  bajoMeta: KpiBajoMeta[] = [];
  /** El alcance del área en el trimestre según el servidor (ejercicioTrimestre). */
  alcance = "";
  semaforoArea: EstadoBsc<SemaforoBsc> | null = null;
  estatus: EstadoBsc<EstatusTrimestreBsc> | null = null;
  /** "15 oct"; vacío sin fecha límite de envío. */
  limiteEnvio = "";
  regreso = "";
  resultado: ResultadoCierre | null = null;
  /** El comentario para Dirección (cerrar.do). */
  comentario = "";

  /* ---------- Lo que se dice al guardar y al enviar ---------- */
  /** Lo último que se guardó (región role="status"). */
  mensajeGuardado = "";
  /** Lo que impidió guardar en el paso 2 o la última consulta silenciosa. */
  errorGuardado = "";
  /** Los avisos del servidor de lo último guardado en el paso 2. */
  avisosGuardado: MensajeCierre[] = [];
  /** El error deja la pantalla desactualizada: el botón es «Recargar». */
  recargar = false;
  guardando = false;
  enviando = false;
  errorEnvio = "";
  /** El error del envío habla de las fechas del ciclo: se ofrece Parámetros. */
  errorDeFechas = false;

  /* ---------- Índices de la respuesta ---------- */
  private kpiPorId = new Map<number, BscEjercicioKpi>();
  private mesesPorKpi = new Map<number, Map<number, BscKpiMes>>();
  private capturaPorKpi = new Map<number, BscKpiTrimestre>();
  /** El error y los avisos del último guardado de cada fila; sobreviven a la consulta que le sigue. */
  private notasFila = new Map<number, { error: string; avisos: MensajeCierre[] }>();

  private viaLeida = false;
  private clave = "";
  private consulta?: Subscription;
  private peticion?: Subscription;
  private ruta$?: Subscription;
  private dialogo?: MatDialogRef<CapturaMesBscComponent, ResultadoCapturaMesBsc>;
  /** La pregunta de «Enviar a revisión» (Swal) también vive fuera de la pantalla, como en T1 y T3. */
  private preguntaAbierta = false;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.verTablero = tieneAccesoMenu(this.authService.getMenuList(), RUTA.tablero);
    this.desdeArea = this.route.snapshot.queryParamMap.get("desde") === "area";
    // Angular reutiliza la pantalla si solo cambia el trimestre de la ruta (el
    // historial del navegador): se abre el que pide.
    this.ruta$ = this.route.paramMap.subscribe((p) => {
      const id = idValido(p.get("idEjercicio"));
      const q = trimestreValido(p.get("trimestre"));
      const clave = `${id}|${q}`;
      if (clave === this.clave) {
        return;
      }
      this.clave = clave;
      this.reinicia(id);
      if (id == null || q == null) {
        this.estado = "ERROR";
        this.error = "El enlace no trae una planeación y un trimestre válidos. Ábrelo desde Mi BSC.";
        this.cdr.detectChanges();
        return;
      }
      this.idEjercicio = id;
      this.q = q;
      this.meses = mesesDeTrimestre(q);
      this.consultar();
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.peticion?.unsubscribe();
    this.ruta$?.unsubscribe();
    // El cuadro y la pregunta viven en el overlay y sobreviven al cambio de ruta.
    this.dialogo?.close();
    if (this.preguntaAbierta && swal.isVisible()) {
      swal.close();
    }
  }

  /** @param id la planeación que se abre: si es otra, su área puede tener otra vía y se vuelve a leer. */
  private reinicia(id: number | null): void {
    this.consulta?.unsubscribe();
    this.peticion?.unsubscribe();
    if (id !== this.idEjercicio) {
      this.viaLeida = false;
      this.via = null;
    }
    this.idEjercicio = null;
    this.estado = "CARGANDO";
    this.error = "";
    this.loading = false;
    this.guardando = false;
    this.enviando = false;
    this.modo = "CIERRE";
    this.paso = 1;
    this.recienEnviado = false;
    this.filasMeses = [];
    this.filasAvance = [];
    this.pie = null;
    this.resultado = null;
    this.comentario = "";
    this.limpiaMensajes();
    this.notasFila = new Map();
  }

  private limpiaMensajes(): void {
    this.mensajeGuardado = "";
    this.errorGuardado = "";
    this.avisosGuardado = [];
    this.recargar = false;
    this.errorEnvio = "";
    this.errorDeFechas = false;
  }

  /* ============================ Consulta ============================ */

  /**
   * previaCierre.do (y, la primera vez, catalogos.do para la vía del área).
   *
   * @param silencioso tras guardar o antes de confirmar: la pantalla se queda
   *        como está (mismo paso, lo escrito sin guardar se conserva).
   * @param despues lo que sigue con la respuesta ya pintada.
   */
  consultar(silencioso = false, despues?: () => void): void {
    const idEjercicio = this.idEjercicio;
    if (idEjercicio == null) {
      return;
    }
    const clave = this.clave;
    this.consulta?.unsubscribe();
    this.loading = true;
    if (!silencioso) {
      this.estado = "CARGANDO";
      this.error = "";
    }
    this.cdr.detectChanges();

    const previa$ = this.bscService.previaCierre(idEjercicio, this.q);
    // La vía no viene en la previa: catalogos.do la trae (una vez; si falla, decide el servidor).
    const peticion$: Observable<[BscRes, BscRes | null]> = this.viaLeida
      ? previa$.pipe(map((res): [BscRes, BscRes | null] => [res, null]))
      : forkJoin([previa$, this.bscService.catalogos().pipe(catchError(() => of(null)))]);

    this.consulta = peticion$.subscribe({
      next: ([res, catalogos]) => {
        if (clave !== this.clave) {
          return;
        }
        this.loading = false;
        if (!res || res.codigoError) {
          this.fallaConsulta(this.mensaje(res, "No se pudo consultar el cierre del trimestre."), silencioso);
          return;
        }
        if (catalogos && !catalogos.codigoError) {
          this.viaLeida = true;
          const area = (catalogos.catalogos?.areas ?? []).find((a) => a.idBscArea === res.ejercicio?.idBscArea);
          this.via = area ? area.viaCaptura ?? "AMBAS" : null;
        }
        this.aplica(res, silencioso);
        this.cdr.detectChanges();
        despues?.();
      },
      error: () => {
        if (clave !== this.clave) {
          return;
        }
        this.loading = false;
        this.fallaConsulta("No se pudo consultar el cierre del trimestre: sin conexión con el servidor.", silencioso);
      },
    });
  }

  /** Una consulta silenciosa que falla deja la pantalla como estaba y lo dice; la primera, el estado ERROR. */
  private fallaConsulta(texto: string, silencioso: boolean): void {
    if (silencioso && this.estado === "DATOS") {
      this.errorGuardado = `${texto} Lo que se ve puede no estar al día.`;
    } else {
      this.estado = "ERROR";
      this.error = texto;
    }
    this.cdr.detectChanges();
  }

  private aplica(res: BscRes, silencioso: boolean): void {
    this.puedeCapturar = res.puedeCapturar === true;
    this.puedeCerrar = res.puedeCerrar === true;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.ciclo = res.ciclo ?? null;
    this.ejercicio = res.ejercicio ?? null;
    this.anio = this.ejercicio?.anio ?? this.ciclo?.anio ?? null;
    this.nombreArea = this.ejercicio?.area ?? "";
    this.idBscArea = this.ejercicio?.idBscArea ?? null;
    this.trimestre = res.ejercicioTrimestre ?? (res.trimestres ?? []).find((t) => t.trimestre === this.q) ?? null;
    const kpis = (res.kpis ?? []).filter((k) => k.idEjercicioKpi != null && k.estatus !== "RETIRADO");

    this.indexa(res, kpis);

    this.faltantes = (res.faltantes ?? []).map(parteMensaje);
    // Los KPI bajo meta tienen su bloque con el ratio: su aviso no se repite.
    this.avisos = (res.avisos ?? []).map(parteMensaje).filter((a) => a.codigo !== "KPI_BAJO_META");
    this.bajoMeta = (res.kpisBajoMeta ?? []).filter((k) => k.idEjercicioKpi != null).map((k) => this.bajoMetaDe(k));

    const t = this.trimestre;
    const est = t?.estatus;
    this.modo = this.modoDe(est);
    this.estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === est) ?? null;
    this.alcance = numero(t?.alcanceReal) == null
      ? "Sin alcance calculado"
      : `${pct(t?.alcanceReal)} de ${pct(t?.alcanceProgramado)} programado`;
    this.semaforoArea = SEMAFORO.find((s) => s.valor === t?.semaforo) ?? null;
    const fechaLimite = (res.trimestresCiclo ?? []).find((f) => f.trimestre === this.q)?.fechaLimiteEnvio ?? t?.fechaLimiteEnvio;
    this.limiteEnvio = fechaLimite ? diaMes(fechaLimite, this.anio) : "";
    this.regreso = "";
    if (est === "REGRESADO") {
      const quien = [t?.usuarioResuelve, diaMes(t?.fechaResolucion, this.anio, "")].filter(Boolean).join(", ");
      const porQuien = quien ? ` (${quien})` : "";
      const motivo = t?.motivo ? `: «${t.motivo}»` : "";
      this.regreso = `Dirección regresó Q${this.q}${porQuien}${motivo}.`
        + " Corrige lo que pidió y vuelve a enviarlo.";
    }

    const anteriores = silencioso ? new Map(this.filasAvance.map((f) => [f.idEjercicioKpi, f])) : new Map<number, FilaAvance>();
    if (!silencioso) {
      this.notasFila = new Map();
    }
    this.filasMeses = kpis.map((k) => this.filaMeses(k));
    this.filasAvance = kpis.map((k) => this.filaAvance(k, anteriores.get(k.idEjercicioKpi as number)));
    this.armaPie();
    this.resultado = this.modo === "ENVIADO" || this.modo === "AUTORIZADO" ? this.armaResultado() : null;

    if (this.modo !== "CIERRE") {
      this.paso = 3;
    } else if (!silencioso) {
      // Con meses pendientes se empieza por ellos; si no, por el avance real (la maqueta).
      this.paso = this.mesesFaltantes > 0 ? 1 : 2;
    }
    this.estado = "DATOS";
  }

  /** Los índices de la respuesta: los KPI por id, sus meses del trimestre y su avance capturado de Q. */
  private indexa(res: BscRes, kpis: BscEjercicioKpi[]): void {
    this.kpiPorId = new Map(kpis.map((k) => [k.idEjercicioKpi as number, k]));
    this.mesesPorKpi = new Map();
    for (const m of res.meses ?? []) {
      if (m.idEjercicioKpi == null || m.mes == null) {
        continue;
      }
      if (!this.mesesPorKpi.has(m.idEjercicioKpi)) {
        this.mesesPorKpi.set(m.idEjercicioKpi, new Map());
      }
      this.mesesPorKpi.get(m.idEjercicioKpi)?.set(m.mes, m);
    }
    this.capturaPorKpi = new Map();
    for (const c of res.capturas ?? []) {
      if (c.idEjercicioKpi != null && (c.trimestre == null || c.trimestre === this.q)) {
        this.capturaPorKpi.set(c.idEjercicioKpi, c);
      }
    }
  }

  /** Qué se hace con el trimestre según su estatus (ver ModoCierre). */
  private modoDe(est: EstatusTrimestreBsc | undefined): ModoCierre {
    if (this.ejercicio?.estatus === "AUTORIZADA" && (est === "EN_CAPTURA" || est === "REGRESADO")) {
      return "CIERRE";
    }
    if (est === "ENVIADO") {
      return "ENVIADO";
    }
    return est === "AUTORIZADO" ? "AUTORIZADO" : "NO_DISPONIBLE";
  }

  /* ============================ Reglas de la pantalla ============================ */

  get historica(): boolean {
    return this.ejercicio?.origen === "IMPORTACION_HISTORICA";
  }

  get cicloCerrado(): boolean {
    return this.ciclo?.estatus === "CERRADO";
  }

  get viaExcel(): boolean {
    return this.via === "EXCEL";
  }

  /** Lo que exige el ciclo para cerrar: los 3 meses de cada KPI vigente (DEFAULT 1). */
  get exigeMeses(): boolean {
    return this.ciclo?.exigirMesesParaCerrar !== false;
  }

  /** En este trimestre se captura (meses en el paso 1, avances en el paso 2). */
  get capturaHabilitada(): boolean {
    return (
      this.modo === "CIERRE" &&
      this.puedeCapturar &&
      !this.viaExcel &&
      !this.historica &&
      !this.cicloCerrado &&
      !this.recargar
    );
  }

  /** Por qué los pasos 1 y 2 son de lectura; vacío si se captura. */
  get notaCaptura(): string {
    if (this.modo !== "CIERRE" || this.capturaHabilitada || this.recargar) {
      return "";
    }
    if (this.cicloCerrado) {
      return `${this.anio ?? "El año"} está cerrado: ya no se capturan avances.`;
    }
    if (this.viaExcel) {
      return "El área captura con su Excel FOR-011: lo que falte se carga en «Cargar Excel» y el trimestre se envía aquí.";
    }
    return "Tu perfil no captura en esta área: puedes revisar y enviar el trimestre, pero no capturar.";
  }

  get titulo(): string {
    const anio = this.anio != null ? " " + this.anio : "";
    return this.nombreArea ? `Cierre de Q${this.q} · ${this.nombreArea}${anio}` : `Cierre de Q${this.q}`;
  }

  /** Bajo el título, como la maqueta: qué está listo y qué falta. */
  get subtitulo(): string {
    if (this.estado === "CARGANDO") {
      return "Consultando el trimestre…";
    }
    if (this.estado === "ERROR") {
      return "No se pudo abrir el cierre del trimestre.";
    }
    switch (this.modo) {
      case "NO_DISPONIBLE":
        return this.faltantes[0]?.texto ?? `Q${this.q} no se puede cerrar ahora.`;
      case "ENVIADO":
        return this.recienEnviado
          ? `Q${this.q} se envió a revisión: Dirección lo autoriza o lo regresa.`
          : `Q${this.q} ya está en revisión de Dirección.`;
      case "AUTORIZADO":
        return `Q${this.q} ya está autorizado: su alcance quedó congelado.`;
    }
    const partes: string[] = [];
    const faltan = this.mesesFaltantes;
    if (faltan === 0) {
      partes.push(`${primeraMayuscula(listaDeMeses(this.meses))} ya están capturados.`);
    } else {
      const kpis = this.kpisConFaltantes;
      partes.push(`Faltan ${faltan} ${faltan === 1 ? "mes" : "meses"} por capturar en ${kpis} ${kpis === 1 ? "KPI" : "KPIs"}.`);
    }
    const sinReal = this.kpisSinReal;
    const palabraKpi = sinReal === 1 ? "KPI" : "KPIs";
    partes.push(
      sinReal === 0
        ? "El avance real del trimestre ya está en cada KPI."
        : `Falta el avance real del trimestre en ${sinReal} ${palabraKpi}.`,
      this.limiteEnvio ? `Límite de envío: ${this.limiteEnvio}.` : "Sin fecha límite de envío.",
    );
    return partes.join(" ");
  }

  get queryMiBsc(): Record<string, number | null> {
    return { anio: this.anio, idBscArea: this.idBscArea };
  }

  get queryArea(): Record<string, string | number | null> {
    return { anio: this.anio, corte: `${this.q}` };
  }

  /** Meses vigentes sin capturar (los NA no cuentan). */
  get mesesFaltantes(): number {
    return this.filasMeses.reduce((n, f) => n + (f.vigente ? f.faltan : 0), 0);
  }

  get kpisConFaltantes(): number {
    return this.filasMeses.filter((f) => f.vigente && f.faltan > 0).length;
  }

  get mesesVigentes(): number {
    return this.filasMeses.filter((f) => f.vigente).length * 3;
  }

  get kpisVigentes(): number {
    return this.filasAvance.filter((f) => f.vigente).length;
  }

  get kpisSinReal(): number {
    return this.filasAvance.filter((f) => f.vigente && f.sinReal).length;
  }

  /** Las filas del paso 2 con algo escrito que no se ha guardado. */
  get sucias(): FilaAvance[] {
    return this.filasAvance.filter((f) => f.vigente && filaSucia(f));
  }

  hecho(n: number): boolean {
    return this.modo === "ENVIADO" || this.modo === "AUTORIZADO" || (this.modo === "CIERRE" && n < this.paso);
  }

  actual(n: number): boolean {
    return this.modo === "CIERRE" && n === this.paso;
  }

  /* ---------- Paso 1 ---------- */

  /** Con el ciclo que exige los meses, no se sigue mientras falte alguno. */
  get puedeSeguirMeses(): boolean {
    return this.modo === "CIERRE" && !this.loading && !(this.exigeMeses && this.mesesFaltantes > 0);
  }

  /** El pie del paso 1: si se sigue y qué pasa con lo que falte. */
  get textoPasoMeses(): string {
    const faltan = this.mesesFaltantes;
    if (faltan === 0) {
      return this.kpisVigentes === 0
        ? `Ningún KPI aplica en Q${this.q}: el trimestre se envía sin avance.`
        : `Los meses de los ${this.kpisVigentes} KPIs vigentes están capturados.`;
    }
    const kpis = this.kpisConFaltantes;
    const cuantos = `${faltan} ${faltan === 1 ? "mes" : "meses"} en ${kpis} ${kpis === 1 ? "KPI" : "KPIs"}`;
    if (this.exigeMeses) {
      return this.capturaHabilitada
        ? `Faltan ${cuantos}: ${this.anio ?? "el año"} exige los tres meses de cada KPI para cerrar. Captúralos para seguir.`
        : `Faltan ${cuantos}: ${this.anio ?? "el año"} exige los tres meses de cada KPI para cerrar.`;
    }
    return `Faltan ${cuantos}. ${this.anio ?? "El año"} no los exige para cerrar: el trimestre se envía con el aviso de meses incompletos.`;
  }

  /* ---------- Paso 2 ---------- */

  get puedeSeguirAvance(): boolean {
    return this.modo === "CIERRE" && !this.loading && !this.guardando && !this.recargar && this.kpisSinReal === 0;
  }

  get textoPasoAvance(): string {
    const sinReal = this.kpisSinReal;
    if (sinReal === 0) {
      return "";
    }
    const kpis = `${sinReal} ${sinReal === 1 ? "KPI" : "KPIs"}`;
    const conExcel = this.viaExcel ? ": se carga con el FOR-011" : "";
    return this.capturaHabilitada
      ? `Falta el avance real de ${kpis}: escríbelo para seguir (0 si no hubo avance).`
      : `Falta el avance real de ${kpis}${conExcel}.`;
  }

  /* ---------- Paso 3 ---------- */

  get comentarioObligatorio(): boolean {
    return this.bajoMeta.length > 0;
  }

  get hayFaltaDeFechas(): boolean {
    return this.faltantes.some((f) => f.codigo === "VAL-BSC-FECHAS");
  }

  /** Algún faltante es de la captura (meses, real o programado): se ofrece volver a esos pasos. */
  get hayFaltaDeCaptura(): boolean {
    return this.faltantes.some((f) => f.codigo === "VAL-BSC-TRIMESTRE");
  }

  get puedeEnviar(): boolean {
    return (
      this.modo === "CIERRE" &&
      this.puedeCerrar &&
      !this.loading &&
      !this.enviando &&
      !this.recargar &&
      this.faltantes.length === 0 &&
      this.sucias.length === 0 &&
      this.comentario.trim().length <= LARGO_COMENTARIO &&
      (!this.comentarioObligatorio || this.comentario.trim().length > 0)
    );
  }

  /** Por qué «Enviar a revisión» está deshabilitado, al lado del botón. */
  get razonNoEnvio(): string {
    if (this.modo !== "CIERRE" || this.enviando) {
      return "";
    }
    if (!this.puedeCerrar) {
      return "Tu perfil no envía trimestres de esta área.";
    }
    if (this.faltantes.length > 0) {
      return "Resuelve lo que impide enviar.";
    }
    if (this.sucias.length > 0) {
      return "Hay avances sin guardar: vuelve al paso 2 y guárdalos.";
    }
    if (this.comentario.trim().length > LARGO_COMENTARIO) {
      return `El comentario pasa de ${LARGO_COMENTARIO} caracteres.`;
    }
    if (this.comentarioObligatorio && !this.comentario.trim()) {
      return "Escribe el comentario para Dirección.";
    }
    return "";
  }

  /* ============================ Pasos ============================ */

  /** De 1 a 2 (lo escrito en el paso 2 sigue ahí). */
  aAvance(): void {
    if (!this.puedeSeguirMeses) {
      return;
    }
    this.vaAPaso(2);
  }

  /** De 2 a 3: guarda lo que cambió y pide la previa al día (los faltantes del servidor). */
  aConfirmar(): void {
    if (!this.puedeSeguirAvance) {
      return;
    }
    if (this.sucias.length > 0) {
      this.guardar(() => this.vaAPaso(3));
      return;
    }
    this.errorGuardado = "";
    this.mensajeGuardado = "";
    this.consultar(true, () => this.vaAPaso(3));
  }

  atras(): void {
    if (this.paso > 1 && !this.guardando && !this.enviando) {
      this.vaAPaso((this.paso - 1) as PasoCierre);
    }
  }

  /** Desde los faltantes del paso 3: al paso que los resuelve. */
  irA(paso: PasoCierre): void {
    if (this.modo === "CIERRE" && !this.guardando && !this.enviando) {
      this.vaAPaso(paso);
    }
  }

  private vaAPaso(paso: PasoCierre): void {
    if (this.modo !== "CIERRE" || this.destruido) {
      return;
    }
    this.paso = paso;
    if (paso !== 2) {
      // Los avisos del último guardado se leen en el paso 2; cada fila conserva los suyos.
      this.avisosGuardado = [];
    }
    this.cdr.detectChanges();
    this.enfoca(`#cierre-bsc-paso-${paso}`);
  }

  /* ============================ Paso 1 · Meses ============================ */

  private filaMeses(k: BscEjercicioKpi): FilaMeses {
    const id = k.idEjercicioKpi as number;
    const vigente = aplicaEn(k, this.q);
    const meses = this.meses.map((mes) => this.celdaMes(id, mes));
    return {
      idEjercicioKpi: id,
      objetivo: (k.objetivo ?? "").trim() || "KPI sin objetivo",
      vigente,
      // Los meses ya dicen su estado: solo el NA lleva nota.
      nota: vigente ? "" : this.textoNa(k),
      meses,
      faltan: vigente ? meses.filter((m) => !m.capturado).length : 0,
    };
  }

  private celdaMes(idEjercicioKpi: number, mes: number): CeldaMes {
    const m = this.mesesPorKpi.get(idEjercicioKpi)?.get(mes);
    const capturado = m?.id != null;
    const estado = ESTADO_MES.find((e) => e.valor === m?.estado) ?? null;
    let detalle: string;
    if (capturado) {
      const fecha = diaMes(m?.fechaModificacion || m?.fechaCreacion, this.anio, "");
      detalle = [fecha, (m?.usuarioAlta ?? "").trim()].filter(Boolean).join(" · ") || "Capturado";
    } else if (m?.estado === "VENCIDO") {
      const dia = this.ciclo?.diaLimiteCapturaMensual;
      detalle = dia == null ? "Sin capturar · vencido" : `Venció el ${diaMes(fechaLimiteDelMes(this.anio, mes, dia), this.anio)}`;
    } else if (m?.estado === "NO_APLICA") {
      detalle = "Solo registra actividad";
    } else {
      detalle = "Sin capturar";
    }
    // Sin separador "----" el FOR-011 dejó todo el texto del mes en la actividad (§6 paso 5).
    const resumen = capturado ? ((m?.evidencia || m?.actividad || m?.textoOriginal) ?? "").trim() : "";
    return { mes, capturado, estado, detalle, resumen };
  }

  /** Los meses vigentes sin capturar, KPI por KPI y en orden: la cadena de «Guardar y seguir». */
  private pendientes(): [number, number][] {
    const lista: [number, number][] = [];
    for (const f of this.filasMeses) {
      if (!f.vigente) {
        continue;
      }
      for (const c of f.meses) {
        if (!c.capturado) {
          lista.push([f.idEjercicioKpi, c.mes]);
        }
      }
    }
    return lista;
  }

  /**
   * Abre el cuadro de captura de C1/A3 en un mes. Con «Guardar y seguir» se
   * abre el siguiente mes pendiente del trimestre (después de volver a
   * consultar), sin repetir los que ya se abrieron en la cadena.
   */
  capturarMes(idEjercicioKpi: number, mes: number, visitados: Set<string> = new Set()): void {
    const k = this.kpiPorId.get(idEjercicioKpi);
    if (!k || this.dialogo || this.destruido || !this.capturaHabilitada || this.loading) {
      return;
    }
    const clave = `${idEjercicioKpi}-${mes}`;
    const siguientes = this.pendientes().filter(([i, m]) => `${i}-${m}` !== clave && !visitados.has(`${i}-${m}`));
    this.mensajeGuardado = "";
    this.dialogo = abrirCapturaMesBsc(this.dialog, {
      kpi: k,
      anio: this.anio,
      area: this.nombreArea,
      mes,
      mesGuardado: this.mesesPorKpi.get(idEjercicioKpi)?.get(mes) ?? null,
      avance: this.capturaPorKpi.get(idEjercicioKpi) ?? null,
      estatusTrimestre: this.trimestre?.estatus ?? null,
      haySiguiente: siguientes.length > 0,
    });
    this.dialogo.afterClosed().subscribe((r) => {
      this.dialogo = undefined;
      if (this.destruido) {
        return;
      }
      if (!r) {
        this.enfocaCelda(idEjercicioKpi, mes);
        return;
      }
      this.mensajeGuardado = r.mensaje;
      if (!r.guardado && !r.recargar) {
        this.cdr.detectChanges();
        return;
      }
      const cadena = new Set(visitados);
      cadena.add(clave);
      // Un error que obliga a recargar (el trimestre se envió, el año se cerró): la consulta lo dice.
      this.consultar(true, () => {
        const sigue = r.siguiente ? this.pendientes().find(([i, m]) => !cadena.has(`${i}-${m}`)) : undefined;
        if (sigue && this.capturaHabilitada) {
          this.capturarMes(sigue[0], sigue[1], cadena);
        } else {
          this.enfocaCelda(idEjercicioKpi, mes);
        }
      });
    });
  }

  private enfocaCelda(idEjercicioKpi: number, mes: number): void {
    this.enfoca(`[data-celda="${idEjercicioKpi}-${mes}"] .cierre-bsc__accion`);
  }

  /* ============================ Paso 2 · Avance real ============================ */

  /**
   * La fila del paso 2. Al volver a consultar (silencioso) lo escrito sin
   * guardar se conserva campo por campo, salvo que el servidor traiga otro
   * valor para ese campo (lo cambió el cuadro de captura o alguien más): ahí
   * manda el servidor.
   */
  private filaAvance(k: BscEjercicioKpi, antes: FilaAvance | undefined): FilaAvance {
    const id = k.idEjercicioKpi as number;
    const q = this.q;
    const c = this.capturaPorKpi.get(id);
    const vigente = aplicaEn(k, q);
    const enPorcentaje = !k.unidad || k.unidad === "%";
    const realInicial = textoPorcentaje(c?.avanceReal);
    const programadoInicial = textoPorcentaje(c?.avanceProgramado ?? 1);
    const comentarioInicial = (c?.comentario ?? "").trim();
    const conserva = (campo: "real" | "programado" | "comentario", inicial: string): string => {
      if (!antes) {
        return inicial;
      }
      const escrito = antes[campo];
      const inicialAntes = inicialDe(antes, campo);
      return escrito.trim() !== inicialAntes && inicial === inicialAntes ? escrito : inicial;
    };
    const partes: string[] = [];
    if (vigente) {
      if (q > 1) {
        partes.push(`Acumulado Q${q - 1}: ${pct(acumRealEn(k, q - 1) ?? 0)}`);
      }
      if (!enPorcentaje || (k.metaTexto ?? "").trim()) {
        partes.push(`meta ${meta(k.metaValor, k.metaTexto, k.unidad)}`);
      }
    }
    const notas = this.notasFila.get(id);
    const f: FilaAvance = {
      idEjercicioKpi: id,
      objetivo: (k.objetivo ?? "").trim() || "KPI sin objetivo",
      vigente,
      nota: vigente ? partes.join(" · ") : this.textoNa(k),
      enPorcentaje,
      peso: pesoEn(k, q),
      pesoTexto: pct(pesoEn(k, q)),
      acumAnterior: q > 1 ? acumRealEn(k, q - 1) ?? 0 : 0,
      acumProg: acumProgEn(k, q),
      real: conserva("real", realInicial),
      realInicial,
      programado: conserva("programado", programadoInicial),
      programadoInicial,
      comentario: conserva("comentario", comentarioInicial),
      comentarioInicial,
      acumReal: 0,
      ponderadoTexto: SIN_DATO,
      acumTexto: SIN_DATO,
      bajoMeta: false,
      sinReal: false,
      ayudaReal: "",
      avisoReal: false,
      ayudaProgramado: "",
      avisoProgramado: false,
      error: notas?.error ?? "",
      avisos: notas?.avisos ?? [],
    };
    recalcula(f, this.verde);
    return f;
  }

  private get verde(): number {
    return numero(this.ciclo?.semaforoVerde) ?? VERDE_POR_OMISION;
  }

  /** Cada tecla: la previa de la fila y el pie, y se borra lo que dijo su último guardado. */
  cambia(f: FilaAvance): void {
    recalcula(f, this.verde);
    f.error = "";
    f.avisos = [];
    this.notasFila.delete(f.idEjercicioKpi);
    if (!this.recargar) {
      this.errorGuardado = "";
    }
    this.mensajeGuardado = "";
    this.armaPie();
  }

  /** El alcance del área con lo escrito: el promedio de L y N de los KPI ACTIVO (BscCalculadora.calcularArea). */
  private armaPie(): void {
    if (this.filasAvance.length === 0) {
      this.pie = null;
      return;
    }
    const area = alcanceArea(
      this.filasAvance.map((f) => ({ acumReal: f.acumReal, acumProg: f.acumProg ?? 0, aplica: f.vigente })),
      this.ciclo?.excluirKpiNa !== false,
    );
    const valor = semaforo(ratio(area.real, area.programado), numero(this.ciclo?.semaforoVerde), numero(this.ciclo?.semaforoAmarillo));
    this.pie = {
      real: pct(area.real),
      programado: pct(area.programado),
      semaforo: area.real == null ? null : SEMAFORO.find((s) => s.valor === valor) ?? null,
      bajoMeta: this.filasAvance.filter((f) => f.vigente && f.bajoMeta).length,
    };
  }

  /** «Guardar borrador»: manda lo que cambió y el trimestre sigue en captura. */
  guardarBorrador(): void {
    if (this.sucias.length === 0) {
      this.mensajeGuardado = "No hay cambios que guardar.";
      this.errorGuardado = "";
      this.cdr.detectChanges();
      return;
    }
    this.guardar();
  }

  /**
   * Manda por real.do, fila por fila y en orden, solo lo que cambió. Antes
   * revisa todo: con un error no se manda nada. Un error de una fila
   * (REAL_FUERA_DE_RANGO, VAL-BSC-CAPTURA) se marca en ella y las demás
   * siguen; uno que deja la pantalla desactualizada detiene lo que falta y
   * ofrece recargar. Después vuelve a consultar en silencio.
   *
   * @param despues sigue solo si todo se guardó sin avisos (el paso 3).
   */
  private guardar(despues?: () => void): void {
    if (this.guardando || !this.capturaHabilitada || this.idEjercicio == null) {
      return;
    }
    const sucias = this.sucias;
    let primera: { fila: FilaAvance; campo: string } | null = null;
    for (const f of sucias) {
      const error = revisaFila(f);
      f.error = error?.texto ?? "";
      if (error && !primera) {
        primera = { fila: f, campo: error.campo };
      }
    }
    if (primera) {
      this.errorGuardado = "Revisa los avances marcados: no se guardó nada.";
      this.mensajeGuardado = "";
      this.cdr.detectChanges();
      this.enfoca(`#cierre-bsc-${primera.campo}-${primera.fila.idEjercicioKpi}`);
      return;
    }
    this.guardando = true;
    this.errorGuardado = "";
    this.mensajeGuardado = "";
    this.avisosGuardado = [];
    this.cdr.detectChanges();
    this.guardaFila(sucias, 0, { guardadas: 0, fallidas: 0 }, despues);
  }

  private guardaFila(
    filas: FilaAvance[],
    i: number,
    cuenta: { guardadas: number; fallidas: number },
    despues?: () => void,
  ): void {
    if (this.destruido) {
      return;
    }
    if (i >= filas.length) {
      this.terminaGuardado(cuenta, despues);
      return;
    }
    const f = filas[i];
    const avance = avanceAMandar(f);
    this.peticion = this.bscService.capturarReal(f.idEjercicioKpi, this.q, avance).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          const codigo = res?.codigoError ?? "";
          const texto = codigo === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res?.mensajeError || `No se pudo guardar el avance de «${f.objetivo}».`;
          if (ERRORES_DE_RECARGA.has(codigo)) {
            this.detenGuardado(cuenta, codigo, texto);
            return;
          }
          f.error = texto;
          this.notasFila.set(f.idEjercicioKpi, { error: texto, avisos: [] });
          cuenta.fallidas++;
          this.guardaFila(filas, i + 1, cuenta, despues);
          return;
        }
        const avisos = (res.avisos ?? []).map(parteMensaje);
        f.avisos = avisos;
        this.notasFila.set(f.idEjercicioKpi, { error: "", avisos });
        this.avisosGuardado.push(...avisos);
        // Lo escrito ya es lo guardado: la consulta que sigue no lo toma como borrador.
        f.realInicial = f.real.trim();
        f.programadoInicial = f.programado.trim();
        f.comentarioInicial = f.comentario.trim();
        cuenta.guardadas++;
        this.guardaFila(filas, i + 1, cuenta, despues);
      },
      error: () => this.detenGuardado(cuenta, "", "Sin conexión con el servidor."),
    });
  }

  private terminaGuardado(cuenta: { guardadas: number; fallidas: number }, despues?: () => void): void {
    this.guardando = false;
    const n = cuenta.guardadas;
    const kpis = n === 1 ? "KPI" : "KPIs";
    this.mensajeGuardado = n > 0
      ? `Se guardó el avance de Q${this.q} de ${n} ${kpis}. Q${this.q} sigue en captura hasta que lo envíes.`
      : "";
    const fallidos = cuenta.fallidas === 1 ? "guardó 1 avance" : `guardaron ${cuenta.fallidas} avances`;
    this.errorGuardado = cuenta.fallidas > 0
      ? `No se ${fallidos}: revisa los marcados.`
      : "";
    // Con avisos del servidor se queda en el paso 2 para leerlos (como el cuadro, que espera a «Listo»).
    const sigue = cuenta.fallidas === 0 && this.avisosGuardado.length === 0 ? despues : undefined;
    if (despues && !sigue && cuenta.fallidas === 0) {
      this.mensajeGuardado += " Revisa los avisos y vuelve a pulsar «Continuar a confirmar».";
    }
    if (n === 0) {
      this.cdr.detectChanges();
      this.enfocaPrimerError();
      return;
    }
    // Guardando, el botón pulsado se deshabilitó y el foco cayó al <body>: vuelve a él.
    this.consultar(true, () => {
      if (sigue) {
        sigue();
      } else if (cuenta.fallidas > 0) {
        this.enfocaPrimerError();
      } else {
        this.enfoca(despues ? ".cierre-bsc__continuar" : ".cierre-bsc__guardar");
      }
    });
  }

  /** Un error que deja la pantalla desactualizada (o sin conexión): no se manda lo que falta. */
  private detenGuardado(cuenta: { guardadas: number; fallidas: number }, codigo: string, texto: string): void {
    this.guardando = false;
    if (codigo === "VAL-BSC-VIA") {
      this.via = "EXCEL";
    }
    const kpis = cuenta.guardadas === 1 ? "KPI" : "KPIs";
    const antes = cuenta.guardadas > 0
      ? `Se guardó el avance de ${cuenta.guardadas} ${kpis}, pero no el resto. `
      : "No se guardó nada. ";
    this.recargar = ERRORES_DE_RECARGA.has(codigo);
    this.errorGuardado = `${antes}${texto}${this.recargar && codigo !== "AUTH-001" ? " Recarga para ver cómo quedó." : ""}`;
    this.mensajeGuardado = "";
    this.cdr.detectChanges();
    this.enfoca(this.recargar ? ".cierre-bsc__recargar" : ".cierre-bsc__guardar");
  }

  private enfocaPrimerError(): void {
    const f = this.filasAvance.find((x) => x.error);
    if (f) {
      this.enfoca(`#cierre-bsc-real-${f.idEjercicioKpi}`);
    }
  }

  /** «Recargar» después de un error que deja la pantalla desactualizada: lo escrito sin guardar se descarta. */
  recargarPantalla(): void {
    this.limpiaMensajes();
    this.filasAvance = [];
    this.consultar();
  }

  /* ============================ Paso 3 · Enviar ============================ */

  private bajoMetaDe(k: BscEjercicioKpi): KpiBajoMeta {
    const l = acumRealEn(k, this.q);
    const n = acumProgEn(k, this.q);
    return {
      idEjercicioKpi: k.idEjercicioKpi as number,
      objetivo: (k.objetivo ?? "").trim() || "KPI sin objetivo",
      texto: `Acumulado ${pct(l)} de ${pct(n)} programado: ${pct(ratio(l, n))} de lo programado (verde desde ${pct(this.verde)}).`,
    };
  }

  /** Pregunta y manda cerrar.do; lo que lo impida lo dice el servidor. */
  enviar(): void {
    if (!this.puedeEnviar) {
      return;
    }
    this.errorEnvio = "";
    this.errorDeFechas = false;
    const tarde = this.avisos.some((a) => a.codigo === "ENVIO_FUERA_DE_FECHA") ? " fuera de fecha" : "";
    const texto =
      `Q${this.q} ${this.anio ?? ""} de ${this.nombreArea} se envía a revisión de Dirección${tarde}. ` +
      "Mientras lo revisa, sus meses y avances ya no se capturan; si hay que corregir algo, Dirección lo regresa.";
    this.preguntaAbierta = true;
    this.msg.questionCall(texto, null, (r: { isConfirmed?: boolean }) => {
      this.preguntaAbierta = false;
      if (!this.destruido && r?.isConfirmed === true) {
        this.enviarYa();
      }
    });
  }

  private enviarYa(): void {
    const idEjercicio = this.idEjercicio;
    if (idEjercicio == null || this.enviando || !this.puedeEnviar) {
      return;
    }
    const clave = this.clave;
    this.enviando = true;
    this.cdr.detectChanges();
    this.peticion = this.bscService.cerrarTrimestre(idEjercicio, this.q, this.comentario.trim() || null).subscribe({
      next: (res) => {
        if (clave !== this.clave) {
          return;
        }
        this.enviando = false;
        if (!res || res.codigoError) {
          this.fallaEnvio(res);
          return;
        }
        this.recienEnviado = true;
        const comentario = this.comentario.trim();
        this.comentario = "";
        this.mensajeGuardado = `Se envió Q${this.q} a revisión de Dirección.`;
        if (res.ejercicioTrimestre) {
          this.aplica(res, false);
          this.cdr.detectChanges();
          this.enfoca("#cierre-bsc-resultado");
          return;
        }
        // Se envió, pero el servidor no pudo releer el año (BscCapturaBuss.sinLectura: solo
        // avisos): aplica perdería el área, el año y el trimestre. Se queda lo que se ve, ya
        // como enviado y con los avisos, y se vuelve a pedir la previa en silencio.
        this.marcaEnviado(res, comentario);
        this.cdr.detectChanges();
        this.enfoca("#cierre-bsc-resultado");
        this.consultar(true);
      },
      error: () => {
        this.enviando = false;
        this.errorEnvio = "No se pudo enviar el trimestre: sin conexión con el servidor. No se envió nada.";
        this.cdr.detectChanges();
        this.enfoca(".cierre-bsc__enviar");
      },
    });
  }

  /** El envío sin la relectura del año: el trimestre que se ve pasa a ENVIADO con lo que se sabe. */
  private marcaEnviado(res: BscRes, comentario: string): void {
    this.trimestre = {
      ...this.trimestre,
      trimestre: this.q,
      estatus: "ENVIADO",
      comentarioEnvio: comentario,
      // Quién y cuándo no vienen; los de un envío anterior (REGRESADO) ya no son.
      fechaEnvio: undefined,
      usuarioEnvia: undefined,
    };
    this.modo = "ENVIADO";
    this.paso = 3;
    this.estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === "ENVIADO") ?? null;
    this.regreso = "";
    this.faltantes = [];
    this.avisos = (res.avisos ?? []).map(parteMensaje).filter((a) => a.codigo !== "KPI_BAJO_META");
    this.resultado = this.armaResultado();
  }

  /**
   * Lo que impidió enviar. VAL-BSC-TRIMESTRE, VAL-BSC-FECHAS y
   * VAL-BSC-CERRADO traen los primeros problemas en palabras: se dicen y se
   * vuelve a pedir la previa para que la lista de lo que falta quede al día.
   * VAL-BSC-CONFLICTO (alguien ya lo envió o lo movió) ofrece recargar, que
   * enseña cómo quedó.
   */
  private fallaEnvio(res: BscRes | null | undefined): void {
    const codigo = res?.codigoError ?? "";
    if (codigo === "AUTH-001") {
      this.errorEnvio = "Tu sesión caducó. Vuelve a entrar. No se envió nada.";
    } else {
      this.errorEnvio = res?.mensajeError || "No se pudo enviar el trimestre. No se envió nada.";
    }
    this.errorDeFechas = codigo === "VAL-BSC-FECHAS";
    if (codigo === "VAL-BSC-CONFLICTO") {
      // El mensaje del servidor ya pide recargar; el botón lo hace.
      this.recargar = true;
      this.cdr.detectChanges();
      this.enfoca(".cierre-bsc__recargar");
      return;
    }
    this.cdr.detectChanges();
    if (["VAL-BSC-TRIMESTRE", "VAL-BSC-FECHAS", "VAL-BSC-CERRADO"].includes(codigo)) {
      this.consultar(true, () => this.enfoca(".cierre-bsc__error-envio"));
    } else {
      this.enfoca(".cierre-bsc__error-envio");
    }
  }

  /* ============================ Resultado ============================ */

  private armaResultado(): ResultadoCierre {
    const t = this.trimestre ?? {};
    const q = this.q;
    const alcance = numero(t.alcanceReal) == null ? SIN_DATO : `${pct(t.alcanceReal)} de ${pct(t.alcanceProgramado)} programado`;
    const sem = SEMAFORO.find((s) => s.valor === t.semaforo) ?? null;
    const semaforoDato: DatoResultado = { etiqueta: "Semáforo", valor: sem ? "" : SIN_DATO, badge: sem };
    const dato = (etiqueta: string, valor: string): DatoResultado => ({ etiqueta, valor });
    if (this.modo === "AUTORIZADO") {
      const importado = t.origen === "IMPORTACION";
      return {
        titulo: importado ? `Q${q} se importó autorizado` : `Q${q} autorizado`,
        icono: "verified",
        estatus: this.estatus,
        datos: importado
          ? [dato("Origen", "Carga del FOR-011 (arranque del año)"), dato("Alcance congelado", alcance), semaforoDato]
          : [
              dato("Lo envió", t.usuarioEnvia || SIN_DATO),
              dato("Enviado", fechaHora(t.fechaEnvio)),
              dato("Lo autorizó", t.usuarioResuelve || SIN_DATO),
              dato("Autorizado", fechaHora(t.fechaResolucion)),
              dato("Alcance congelado", alcance),
              semaforoDato,
            ],
        comentarioTitulo: "Comentario de Dirección",
        comentario: (t.motivo ?? "").trim(),
        siguiente: "Su alcance quedó congelado y es el que leen el tablero y el FOR-011. Si hay que corregir algo, Dirección lo reabre.",
      };
    }
    const siguiente = q < 4
      ? `Dirección lo revisa en «Revisión BSC»: lo autoriza (su alcance queda congelado y Q${q + 1} se abre a cierre) o lo regresa con un motivo. El aviso te llega a las notificaciones del COS.`
      : "Dirección lo revisa en «Revisión BSC»: lo autoriza (su alcance queda congelado; es el último trimestre del año) o lo regresa con un motivo. El aviso te llega a las notificaciones del COS.";
    return {
      titulo: this.recienEnviado ? `Q${q} enviado a revisión` : `Q${q} ya está en revisión`,
      icono: this.recienEnviado ? "task_alt" : "send",
      estatus: this.estatus,
      datos: [
        dato("Lo envió", t.usuarioEnvia || SIN_DATO),
        dato("Enviado", fechaHora(t.fechaEnvio)),
        dato("Límite de envío", this.limiteEnvio || "Sin fecha límite"),
        dato("Alcance del área", alcance),
        semaforoDato,
      ],
      comentarioTitulo: "Comentario para Dirección",
      comentario: (t.comentarioEnvio ?? "").trim(),
      siguiente,
    };
  }

  /* ============================ Utilidades ============================ */

  private textoNa(k: BscEjercicioKpi): string {
    const inicio = k.trimestreInicio ?? 1;
    return inicio > this.q ? `Aplica desde Q${inicio} · no cuenta en Q${this.q}` : `NA en Q${this.q} · no cuenta`;
  }

  textoMes(mes: number): string {
    return mesEnTexto(mes);
  }

  nombreDeMes(mes: number): string {
    return nombreMes(mes);
  }

  trackMensaje(_: number, m: MensajeCierre): string {
    return `${m.codigo}|${m.texto}`;
  }

  trackFila(_: number, f: { idEjercicioKpi: number }): number {
    return f.idEjercicioKpi;
  }

  trackCelda(_: number, c: CeldaMes): number {
    return c.mes;
  }

  trackDato(_: number, d: DatoResultado): string {
    return d.etiqueta;
  }

  private mensaje(res: BscRes | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || porOmision;
  }

  /** Después de pintar: el elemento pudo estar deshabilitado o no existir todavía. */
  private enfoca(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }
}

/* ============================ Funciones puras ============================ */

function idValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 0 ? n : null;
}

function trimestreValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n >= 1 && n <= 4 ? n : null;
}

/** "CODIGO Q2: mensaje" → { codigo, mensaje } (el trimestre ya lo dice la pantalla); sin código, tal cual. */
function parteMensaje(texto: string): MensajeCierre {
  const m = FORMATO_MENSAJE.exec(texto ?? "");
  return m ? { codigo: m[1], texto: m[3] } : { codigo: "", texto: texto ?? "" };
}

function filaSucia(f: FilaAvance): boolean {
  return (
    f.real.trim() !== f.realInicial ||
    f.programado.trim() !== f.programadoInicial ||
    f.comentario.trim() !== f.comentarioInicial
  );
}

/**
 * La previa de la fila con lo escrito: K = I × M / J (bsc-calculos.ponderado)
 * y L = L del trimestre anterior + K contra N; sin real, K = 0 como el motor.
 * Las ayudas dicen lo mismo que el cuadro de captura.
 */
function recalcula(f: FilaAvance, verde: number): void {
  if (!f.vigente) {
    f.acumReal = f.acumAnterior;
    f.ponderadoTexto = SIN_DATO;
    f.acumTexto = SIN_DATO;
    f.bajoMeta = false;
    f.sinReal = false;
    return;
  }
  const r = leePorcentaje(f.real);
  const p = leePorcentaje(f.programado);
  // Lo que no se puede mandar no entra a la previa; arriba de 100 en % sí (sobrecumplimiento).
  const fuera = r.valor != null && realFuera(f, r.valor);
  const real = r.error || r.valor == null || fuera ? null : redondear(r.valor / 100);
  const programado = p.error || p.valor == null || p.valor < 0 ? 1 : redondear(p.valor / 100);
  const k = real == null ? null : ponderado(real, f.peso, programado);
  f.acumReal = redondear(f.acumAnterior + (k ?? 0));
  f.ponderadoTexto = k == null ? SIN_DATO : pct(k);
  f.acumTexto = pct(f.acumReal);
  const rt = ratio(f.acumReal, f.acumProg);
  f.sinReal = !r.error && r.valor == null;
  // Sin real no se marca: falta capturarlo (el pie y el paso 3 lo dicen aparte).
  f.bajoMeta = rt != null && rt < verde && real != null;
  ayudaDelReal(f, r);
  ayudaDelProgramado(f, p);
}

/** La ayuda bajo el campo del real (y si se pinta como aviso): lo mismo que dice el cuadro de captura. */
function ayudaDelReal(f: FilaAvance, r: LecturaNumero): void {
  f.avisoReal = true;
  if (r.error) {
    f.ayudaReal = r.error;
  } else if (r.valor == null) {
    f.ayudaReal = f.realInicial ? "Lo capturado no se borra: corrígelo." : "";
    f.avisoReal = !!f.realInicial;
  } else if (r.valor < 0) {
    f.ayudaReal = "No puede ser negativo.";
  } else if (realFuera(f, r.valor)) {
    // Lo que no se puede mandar (arriba del tope en %, o demasiado grande en No o días) no promete aviso.
    f.ayudaReal = textoRealFuera(r.valor, f.enPorcentaje);
  } else if (r.valor > 100) {
    f.ayudaReal = "Supera lo programado (sobrecumplimiento): se guarda con aviso.";
  } else if (r.valor > 0 && r.valor < 1) {
    const cien = formatoNumero(r.valor * 100);
    f.ayudaReal = `Es ${formatoNumero(r.valor)} %: si querías decir ${cien} %, escribe ${cien}.`;
  } else {
    f.ayudaReal = "";
    f.avisoReal = false;
  }
}

/** La ayuda bajo el campo del programado (J), donde lo habitual es 100 %. */
function ayudaDelProgramado(f: FilaAvance, p: LecturaNumero): void {
  f.avisoProgramado = true;
  if (p.error) {
    f.ayudaProgramado = p.error;
  } else if (p.valor == null) {
    f.ayudaProgramado = "Lo habitual es 100.";
  } else if (p.valor < 0) {
    f.ayudaProgramado = "No puede ser negativo.";
  } else if (p.valor === 0) {
    f.ayudaProgramado = "Con 0 el ponderado es 0: se guarda con aviso.";
  } else if (p.valor !== 100) {
    f.ayudaProgramado = "Distinto de 100 %: se guarda con aviso.";
  } else {
    f.ayudaProgramado = "";
    f.avisoProgramado = false;
  }
}

/** Lo que impide mandar la fila, con el campo al que va el foco; nulo si se puede. Las reglas del cuadro de captura. */
function revisaFila(f: FilaAvance): { texto: string; campo: string } | null {
  const real = revisaReal(f);
  if (real) {
    return real;
  }
  return revisaProgramadoYResumen(f);
}

/** revisaFila del avance real: lo que no se entiende, lo capturado que se borró o lo que no se puede mandar. */
function revisaReal(f: FilaAvance): { texto: string; campo: string } | null {
  const r = leePorcentaje(f.real);
  if (r.error) {
    return { texto: `Avance real: ${r.error}`, campo: "real" };
  }
  if (r.valor == null && f.realInicial) {
    return { texto: "El avance real ya capturado no se borra: corrígelo (0 si no hubo avance).", campo: "real" };
  }
  if (r.valor != null && realFuera(f, r.valor)) {
    return { texto: textoRealFuera(r.valor, f.enPorcentaje), campo: "real" };
  }
  return null;
}

/**
 * El real escrito en la fila no se puede mandar: negativo, arriba de
 * TOPE_REAL_EN_PORCENTAJE en % (arriba de 100 sí: sobrecumplimiento, con
 * aviso) o demasiado grande. Las reglas del cuadro de captura.
 */
function realFuera(f: FilaAvance, valor: number): boolean {
  return valor < 0 || (f.enPorcentaje && valor > TOPE_REAL_EN_PORCENTAJE) || valor >= TOPE_PORCENTAJE;
}

/** Por qué el avance real escrito no se puede mandar: negativo, arriba del tope en % o demasiado grande. */
function textoRealFuera(valor: number, enPorcentaje: boolean): string {
  if (valor < 0) {
    return "El avance real no puede ser negativo.";
  }
  return enPorcentaje ? `En % el avance real va de 0 a ${TOPE_REAL_EN_PORCENTAJE}.` : "El avance real es demasiado grande.";
}

/** revisaFila del programado y del resumen de evidencia, ya con el real en orden. */
function revisaProgramadoYResumen(f: FilaAvance): { texto: string; campo: string } | null {
  const p = leePorcentaje(f.programado);
  if (p.error) {
    return { texto: `Programado: ${p.error}`, campo: "programado" };
  }
  if (p.valor == null && f.programado.trim() !== f.programadoInicial) {
    return { texto: "Escribe el programado del trimestre (lo habitual es 100).", campo: "programado" };
  }
  if (p.valor != null && (p.valor < 0 || p.valor >= TOPE_PORCENTAJE)) {
    return {
      texto: p.valor < 0 ? "El programado no puede ser negativo." : "El programado es demasiado grande.",
      campo: "programado",
    };
  }
  if (f.comentario.trim().length > LARGO_COMENTARIO) {
    return { texto: `El resumen de evidencia pasa de ${LARGO_COMENTARIO} caracteres.`, campo: "comentario" };
  }
  return null;
}

/** Solo lo que cambió: lo que no viaja, el servidor lo conserva. El comentario vacío lo borra. */
function avanceAMandar(f: FilaAvance): BscAvanceCaptura {
  const avance: BscAvanceCaptura = {};
  if (f.real.trim() !== f.realInicial) {
    const r = leePorcentaje(f.real).valor;
    avance.avanceReal = r == null ? null : redondear(r / 100);
  }
  if (f.programado.trim() !== f.programadoInicial) {
    const p = leePorcentaje(f.programado).valor;
    avance.avanceProgramado = p == null ? null : redondear(p / 100);
  }
  if (f.comentario.trim() !== f.comentarioInicial) {
    avance.comentario = f.comentario.trim();
  }
  return avance;
}

/** "julio, agosto y septiembre". */
function listaDeMeses(meses: number[]): string {
  const nombres = meses.map((m) => mesEnTexto(m));
  return nombres.length > 1 ? `${nombres.slice(0, -1).join(", ")} y ${nombres.at(-1)}` : nombres.join("");
}

/** Lo que tenía guardado ese campo de la fila (filaAvance, al conservar lo escrito sin guardar). */
function inicialDe(f: FilaAvance, campo: "real" | "programado" | "comentario"): string {
  if (campo === "real") {
    return f.realInicial;
  }
  return campo === "programado" ? f.programadoInicial : f.comentarioInicial;
}

function primeraMayuscula(texto: string): string {
  return texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;
}

/**
 * La fecha límite de la captura de un mes (la del VENCIDO de A3 y del hito
 * CAPTURA_MES): fin de mes + día límite del ciclo, en `yyyy-MM-dd`. La misma
 * de Mi BSC.
 */
function fechaLimiteDelMes(anio: number | null, mes: number, dia: number): string {
  const a = anio ?? new Date().getFullYear();
  const fin = new Date(a, mes, 0);
  fin.setDate(fin.getDate() + dia);
  return `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, "0")}-${String(fin.getDate()).padStart(2, "0")}`;
}
